import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import type { ConfigDto, MobilePortingConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";
import SwissMobileNumberEntry from "../../Shared/SwissMobileNumberEntry";
import VueDatePicker from "../../Shared/LazyDatePicker";
import { de } from "date-fns/locale";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

// Analog zur Festnetz-Portierung: entweder vor Ablauf der Mindestvertragsdauer (min. 7 Arbeitstage)
// oder unter Einhaltung der Vertragsdauer (höchstens 365 Tage vor Vertragsablauf)
const PORTING_TYPE_NOT_COMPLY = "porting_not_comply_with_contract";
const PORTING_TYPE_COMPLY = "porting_comply_with_contract";
const MIN_BUSINESS_DAYS = 7;
const MAX_CONTRACT_EXPIRY_DAYS = 365;

interface Props {
  config: ConfigDto;
}

export default defineComponent({
  template: html,
  components: { ErrorMessage, SwissMobileNumberEntry, VueDatePicker },
  props: {
    config: {
      type: Object as () => ConfigDto,
      required: true,
    },
  },
  setup(props: Props) {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);

    // vars und computed
    const generalError = ref("");
    const mobileNumber = ref<string | null>(null);
    const mobileNumberError = ref("");
    const portingType = ref<string>(PORTING_TYPE_NOT_COMPLY);
    const portingDate = ref<Date | null>(null);
    const portingDateError = ref("");
    const portingTime = ref<string | null>(null);
    const previousProvider = ref<string | null>(null);
    const previousSubscriptionType = ref<string | null>(null);

    const portingTypeChoices = computed(() => [
      {
        key: PORTING_TYPE_NOT_COMPLY,
        text: t("ConfigsMobile_Porting_Type_NotComply"),
        info: t("ConfigsMobile_Porting_Type_NotComply_Info"),
      },
      {
        key: PORTING_TYPE_COMPLY,
        text: t("ConfigsMobile_Porting_Type_Comply"),
        info: t("ConfigsMobile_Porting_Type_Comply_Info"),
      },
    ]);

    // methods
    const addBusinessDays = (date: Date, days: number): Date => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (0 != day && 6 != day) {
          added++;
        }
      }
      return result;
    };

    const disabledDates = (dt: Date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (PORTING_TYPE_COMPLY == portingType.value) {
        const latest = new Date(today);
        latest.setDate(latest.getDate() + MAX_CONTRACT_EXPIRY_DAYS);
        return dt < today || dt > latest;
      }

      const earliest = addBusinessDays(today, MIN_BUSINESS_DAYS);
      return dt < earliest;
    };

    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        mobileNumberError.value = "";
        portingDateError.value = "";
        generalError.value = "";
        if (orderService == null) {
          return;
        }
        const portingDateStr = ((): string | null => {
          if (null == portingDate.value) {
            return null;
          }
          const yyyy = portingDate.value.getFullYear();
          const mm = String(portingDate.value.getMonth() + 1).padStart(2, "0");
          const dd = String(portingDate.value.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        })();
        const request: MobilePortingConfigPatchRequest = {
          mobile_number: mobileNumber.value,
          porting_type: portingType.value,
          porting_date: portingDateStr,
          porting_time: portingTime.value,
          previous_provider: previousProvider.value,
          previous_subscription_type: previousSubscriptionType.value,
        };
        try {
          await orderService.patchConfig(props.config.uuid, props.config.type, request);
        } catch (err) {
          if (err instanceof ApiValidationError) {
            mobileNumberError.value = err.errors["mobile_number"]?.[0] ?? "";
            portingDateError.value = err.errors["porting_date"]?.[0] ?? "";
          } else {
            generalError.value = `${err}`;
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    };

    const onPortingTypeChange = async () => {
      await save();
    };

    const onUpdatePortingDate = async (evt: Date | null) => {
      portingDate.value = evt;
      await save();
    };

    const onFieldChange = async () => {
      await save();
    };

    const isValid = computed(() => {
      if (!mobileNumber.value || !portingDate.value) {
        return false;
      }
      return !mobileNumberError.value && !portingDateError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (!mobileNumber.value) {
        mobileNumberError.value = t("Validation_Field_Required");
      }
      if (!portingDate.value) {
        portingDateError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    onMounted(() => {
      mobileNumber.value = props.config.data?.mobile_number ?? null;
      portingType.value = props.config.data?.porting_type ?? PORTING_TYPE_NOT_COMPLY;
      portingDate.value =
        null == props.config.data?.porting_date ? null : new Date(props.config.data.porting_date);
      portingTime.value = props.config.data?.porting_time ?? null;
      previousProvider.value = props.config.data?.previous_provider ?? null;
      previousSubscriptionType.value = props.config.data?.previous_subscription_type ?? null;
    });

    return {
      t,
      de,
      generalError,
      mobileNumber,
      mobileNumberError,
      portingType,
      portingTypeChoices,
      portingDate,
      portingDateError,
      portingTime,
      previousProvider,
      previousSubscriptionType,
      disabledDates,
      onPortingTypeChange,
      onUpdatePortingDate,
      onFieldChange,
      isValid,
      validate,
    };
  },
} as any);
